import { useEffect, useState } from 'react';
import useSound from 'use-sound';
import { isMobile } from 'react-device-detect';

import { styled } from 'styled-components';

import { _useDispatch, _useSelector } from '@src/state/hooks';

import bg from '@src/asset/background/131.png';
import white from '@src/asset/background/white.png';
import blue from '@src/asset/background/blue.png';
import red from '@src/asset/background/red.png';
import green from '@src/asset/background/green.png';

import ChatWidget from '@src/components/ChatWidget';
import Hand from './Handv2';
import CurrentCard from './CurrentCard';
import ActionButtons from './ActionButtons';
import PlayerList from './PlayerList';
import ChooseColorPanel from './ChooseColorPanel';
import Toast from '@src/components/Toast';

import { pushEventQueue } from '@src/state/slices/slice_game';
import {
  getCurrentGame,
  getCurrentChat,
} from '@src/state/selectors/selector_game';

import playCardSound from '@src/asset/sound/playCard.wav';
import imcomingCardSound from '@src/asset/sound/incomingCard.wav';
import moment from 'moment';
import { Player } from '@src/lib/types';
import _ from 'lodash';

const TOAST_TIMEOUT = 2000;

const backgrounds = {
  white: white,
  blue: blue,
  red: red,
  green: green,
  black: bg,
};

const Container = styled.div<{ gamecolor: string | null }>`
  position: relative;
  width: 100%;
  max-height: 100%;

  display: flex;
  flex-grow: 1;

  // align-items: stretch;
  justify-content: flex-end;

  flex-direction: column;

  overflow-y: hidden;
  overflow-x: hidden;

  background-color: #c0c0c0;

  background-repeat: repeat;
  background-image: url(${(props) =>
    props.gamecolor === null
      ? bg
      : backgrounds[props.gamecolor as keyof typeof backgrounds]});
  background-size: ${(props) =>
    props.gamecolor === null || props.gamecolor === 'black' ? '' : 'cover'};
  background-repeat: ${(props) =>
    props.gamecolor === null || props.gamecolor === 'black'
      ? 'tile'
      : 'no-repeat'};
  transition: background 0.3s ease-in-out;
`;

export default function GameView() {
  // state
  const [shouldPlayPlayCardSound, setShouldPlayPlayCardSound] = useState(false);

  // sounds
  const [playIncomingCard] = useSound(imcomingCardSound, { volume: 0.4 });
  const [playPlayCardSound] = useSound(playCardSound, { volume: 0.4 });

  // dispatch
  const dispatch = _useDispatch();

  // meta
  const game = _useSelector(getCurrentGame) || null;
  const chat = _useSelector(getCurrentChat) || null;
  const viewMeta = _useSelector((state) => state.app.stageMeta);
  const playerMeta = _useSelector((state) => state.game.playerMeta);
  const watchingGame =
    _useSelector((state) => state.game.watchingGameId) === null ? false : true;

  // player data

  const playerId = playerMeta ? playerMeta.playerId : '';

  // core game data
  const currentColor = game?.currentColor ? game.currentColor : null;
  const currentCard = game?.currentCard ? game.currentCard : null;
  const players = game?.players ? game.players : null;
  const cards = game?.cards ? game.cards : null;

  // game state
  const gameStarted = game?.gameStart ? game.gameStart : false;
  const gameTerminated = game?.terminated || !game;
  const gameEnded = game?.gameEnded ? game.gameEnded : false;
  const gameWinner = game?.gameWinner
    ? getWinnerName(game.gameWinner, game.players)
    : null;
  const gameIsReversed = game?.isReversed ? game.isReversed : false;

  const playerIsHost =
    game?.hostPlayerId && !watchingGame
      ? game.hostPlayerId === playerId
      : false;
  const isPlayersTurn =
    game?.isPlayersTurn && gameStarted && !watchingGame
      ? game.isPlayersTurn
      : false;
  const playerChoosingColor = game?.playerChoosingColor
    ? game.playerChoosingColor
    : null;
  const isPlayerChoosingColor =
    game?.playerChoosingColor && playerId
      ? game.playerChoosingColor === playerId
      : false;
  const currentPlayerTurn =
    game && typeof game.currentPlayerTurn === 'number'
      ? game.currentPlayerTurn
      : null;
  const currentPlayerTurnExpiresAt = game?.currentPlayerTurnExpiresAt
    ? game.currentPlayerTurnExpiresAt
    : null;

  const poolPlayers =
    game?.type === 'tournament' ? game.poolPlayers : undefined;

  const now = moment();
  const gameCreatedAt = moment(game?.createdAt);
  const fiveMinutes = moment.duration(5, 'minutes');
  const untilStart =
    game?.type === 'tournament'
      ? moment.duration(
          Math.max(
            0,
            fiveMinutes.asSeconds() - now.diff(gameCreatedAt, 'seconds'),
          ),
          'seconds',
        )
      : null;

  const canStart =
    game?.type === 'tournament'
      ? game.poolPlayers?.length === game.players?.length ||
        now.isAfter(gameCreatedAt.add(5, 'minutes'))
      : playerIsHost;

  useEffect(() => {
    if (shouldPlayPlayCardSound) {
      playPlayCardSound();
      setShouldPlayPlayCardSound(false);
      return;
    }
    playIncomingCard();
  }, [currentCard]);

  // manage events
  useEffect(() => {
    if (isPlayersTurn) {
      dispatch(
        pushEventQueue({
          message: 'YOUR TURN',
          showToast: true,
          sound: 'yourTurn',
        }),
      );
    }
  }, [currentPlayerTurn]);

  return (
    <Container gamecolor={currentColor}>
      <Toast gameColor={currentColor} timeout={TOAST_TIMEOUT} />
      {isPlayerChoosingColor && <ChooseColorPanel gameId={viewMeta.gameId} />}
      <PlayerList
        gameIsReversed={gameIsReversed}
        players={players}
        currentPlayerTurn={currentPlayerTurn}
        playerId={playerId}
        playerChoosingColor={playerChoosingColor}
        currentPlayerTurnExpiresAt={currentPlayerTurnExpiresAt}
        poolPlayers={poolPlayers}
      />
      <ChatWidget
        gameId={viewMeta.gameId}
        chat={chat || []}
        chatboxPosition='bottom'
        initialChatboxVisible={!isMobile && !watchingGame}
        initialChatMuted={false}
      />
      <CurrentCard card={currentCard} />
      <ActionButtons
        gameId={viewMeta.gameId}
        gameStarted={gameStarted}
        canStart={canStart}
        isPlayersTurn={isPlayersTurn}
        gameTerminated={gameTerminated}
        gameWinner={gameWinner}
        gameEnded={gameEnded}
        untilStart={untilStart}
        gameWager={0 /* TODO: Get correct wager amount*/}
        watchingGame={watchingGame}
      />
      <Hand
        cards={cards}
        isPlayersTurn={isPlayersTurn}
        gameId={game?.gameId ? game.gameId : null}
        setShouldPlaySound={setShouldPlayPlayCardSound}
      />
    </Container>
  );
}

function getWinnerName(winnerId: string, players: Player[]) {
  const winner = players.find((p) => p.playerId === winnerId);
  if (!winner) return winnerId;

  return winner.name;
}
